import { useEffect, useState } from 'react';
import type { Dicom } from '@/models/dicom';
import { getNativeFps } from '../../utils/frameRate';

/**
 * Resolve a pane's native frame rate. Uses the backend-provided fields first
 * (`framesPerSecond` / `frameTime`); when those are absent it extracts the rate
 * from the decoded video (`numberOfFrames / duration`) once metadata loads.
 *
 * Returns `0` until a rate is known so callers can hide the FPS UI.
 */
export function usePaneNativeFps(
  dicom: Dicom | null,
  viewport: unknown | null,
  isVideoViewport: boolean
): number {
  const dicomFps = dicom ? getNativeFps(dicom) : 0;
  const [videoDuration, setVideoDuration] = useState(0);

  useEffect(() => {
    // Backend already gave us a rate, or this isn't a video — no extraction needed.
    if (dicomFps > 0 || !isVideoViewport || !viewport) {
      setVideoDuration(0);
      return undefined;
    }

    const { videoElement } = viewport as unknown as {
      videoElement?: HTMLVideoElement;
    };
    if (!videoElement) return undefined;

    const readDuration = () => {
      if (Number.isFinite(videoElement.duration) && videoElement.duration > 0) {
        setVideoDuration(videoElement.duration);
      }
    };

    readDuration();
    videoElement.addEventListener('loadedmetadata', readDuration);
    videoElement.addEventListener('durationchange', readDuration);
    return () => {
      videoElement.removeEventListener('loadedmetadata', readDuration);
      videoElement.removeEventListener('durationchange', readDuration);
    };
    // `viewport` identity changes when the video is (re)mounted, re-running this.
  }, [dicomFps, isVideoViewport, viewport]);

  if (!dicom) return 0;
  return getNativeFps(dicom, videoDuration || undefined);
}
